'use client';

import { CardWithImage } from '@/components/ui/card-with-image';
import { cn } from '@/lib/utils';
import { useCallback, useRef, useState } from 'react';
import type { Card, CardRow } from './cards-section';
import styles from './rows-carousel.module.scss';

type RowsCarouselProps = {
  rows: CardRow[];
  className?: string;
  isBackgroundDark?: boolean;
};

export const RowsCarousel = ({ rows, className, isBackgroundDark = true }: RowsCarouselProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const allCards: Card[] = rows.flatMap((row) => row.cards);
  const totalCards = allCards.length;

  // Card width (250px) + gap (16px)
  const SLIDE_WIDTH = 266;

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const index = Math.round(el.scrollLeft / SLIDE_WIDTH);
    setActiveIndex(Math.min(Math.max(index, 0), totalCards - 1));
  }, [totalCards]);

  return (
    <div className={cn(styles.wrapper, !isBackgroundDark && styles['wrapper--light'], className)}>
      <div className={styles.dots}>
        {allCards.map((_, i) => (
          <span key={i} className={cn(styles.dot, i === activeIndex && styles.dot_active)} />
        ))}
      </div>
      <div ref={scrollRef} className={styles.carousel} onScroll={handleScroll}>
        {allCards.map((card, index) => (
          <div key={`${card.title}-${index}`} className={styles.carousel_item}>
            <CardWithImage
              title={card.title}
              titleTag={card.titleTag}
              description={card.description}
              link={card.link}
              linkLabel={card.linkLabel}
              newWindow={card.newWindow}
              image={card.image}
              lottieJson={card.lottieJson}
              videoEmbedUrl={card.videoEmbedUrl}
              embedCode={card.embedCode}
              type={card.type as 'link' | 'button'}
              destination={card.destination}
              ctaId={card.ctaId}
              formContent={card.formContent}
              cardCount={1}
              bgVariant={card.bgVariant}
              buttonStyle={card.buttonStyle}
              contentDirection="column"
              imageType={card.imageType}
              centered={card.centered}
              centeredMobile={card.centeredMobile}
              listStyle={card.listStyle}
              mobileContentFirst={true}
              carouselMode={true}
              fullWidthImage={card.fullWidthImage}
            />
          </div>
        ))}
      </div>
    </div>
  );
};
